import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import employeeService from '../services/employeeService';
import api from '../utils/axios';
import { 
  UserIcon, 
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const EmployeeDashboardNew = () => {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    myCreated: 0,
    assignedToMe: 0,
    departmentLeads: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, my-created, assigned-to-me, department
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (employee) {
      loadLeads();
      loadStats();
    }
  }, [employee, filter]);

  const checkAuth = async () => {
    const token = localStorage.getItem('employeeToken');
    const employeeData = localStorage.getItem('employeeData');

    if (!token || !employeeData) {
      navigate('/employee-login');
      return;
    }

    try {
      const parsedEmployee = JSON.parse(employeeData);
      setEmployee(parsedEmployee);
      setPermissions(parsedEmployee.permissions || {});
    } catch (error) {
      console.log('📊 Fetching leads for employee:', parsedEmployee.teamMemberName);
      console.log('📊 Branch:', parsedEmployee.branch?.name);
      console.log('📊 Filter:', filter);
      navigate('/employee-login');
    }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await api.get(`/department-leads/my-leads?filter=${filter}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setLeads(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await api.get('/department-leads/stats', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const statsData = response.data.data;
        
        // Calculate totals from aggregated data
        const myCreatedTotal = statsData.myCreated?.reduce((sum, item) => sum + item.count, 0) || 0;
        const assignedToMeTotal = statsData.assignedToMe?.reduce((sum, item) => sum + item.count, 0) || 0;
        const branchTotal = statsData.branch?.reduce((sum, item) => sum + item.count, 0) || 0;

        setStats({
          total: myCreatedTotal + assignedToMeTotal,
          myCreated: myCreatedTotal,
          assignedToMe: assignedToMeTotal,
          branchLeads: branchTotal
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleLogout = () => {
    employeeService.logout();
    navigate('/employee-login');
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-green-100 text-green-800',
      proposal: 'bg-purple-100 text-purple-800',
      negotiation: 'bg-orange-100 text-orange-800',
      won: 'bg-green-200 text-green-900',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (!employee) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Employee Dashboard
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Welcome back, {employee.name}!
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{employee.name}</p>
                <p className="text-xs text-gray-600">{employee.branch?.name || 'No Branch'}</p>
                <p className="text-xs text-gray-500">{employee.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Leads</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">My Created</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{stats.myCreated}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <PlusIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Assigned to Me</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">{stats.assignedToMe}</p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <UsersIcon className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>

          {permissions.canViewAllLeads && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Branch Leads</p>
                  <p className="text-3xl font-bold text-orange-600 mt-2">{stats.branchLeads}</p>
                </div>
                <div className="bg-orange-100 rounded-full p-3">
                  <ChartBarIcon className="h-8 w-8 text-orange-600" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Permissions Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Your Permissions:</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {permissions.canAddLead && <span className="text-green-700">✓ Add Leads</span>}
            {permissions.canEditLead && <span className="text-green-700">✓ Edit Leads</span>}
            {permissions.canDeleteLead && <span className="text-green-700">✓ Delete Leads</span>}
            {permissions.canViewAllLeads && <span className="text-green-700">✓ View All Branch Leads</span>}
            {permissions.canGenerateQuotation && <span className="text-green-700">✓ Generate Quotations</span>}
            {permissions.canSendCatalog && <span className="text-green-700">✓ Send Catalogs</span>}
            {permissions.canManageFollowUps && <span className="text-green-700">✓ Manage Follow-ups</span>}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center space-x-2 overflow-x-auto">
            <FunnelIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All My Leads ({stats.total})
            </button>
            <button
              onClick={() => setFilter('my-created')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap ${
                filter === 'my-created'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Created by Me ({stats.myCreated})
            </button>
            <button
              onClick={() => setFilter('assigned-to-me')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap ${
                filter === 'assigned-to-me'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Assigned to Me ({stats.assignedToMe})
            </button>
            {permissions.canViewAllLeads && (
              <button
                onClick={() => setFilter('branch')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap ${
                  filter === 'branch'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Branch Leads ({stats.branchLeads})
              </button>
            )}
          </div>
        </div>

        {/* Leads List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {filter === 'all' && 'All My Leads'}
              {filter === 'my-created' && 'Leads Created by Me'}
              {filter === 'assigned-to-me' && 'Leads Assigned to Me'}
              {filter === 'branch' && 'Branch Leads'}
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Leads Found</h3>
              <p className="text-gray-600">
                {filter === 'my-created' && 'You haven\'t created any leads yet.'}
                {filter === 'assigned-to-me' && 'No leads have been assigned to you yet.'}
                {filter === 'branch' && 'No leads in your branch yet.'}
                {filter === 'all' && 'No leads available.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <tr key={lead._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {lead.customerName || lead.name || 'Unknown'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {lead.customerCompany || lead.company || 'No company'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead.email}</div>
                        <div className="text-sm text-gray-500">{lead.contactNumber || lead.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                          {lead.status || 'new'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{lead.value?.toLocaleString() || '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.sourceOfLead || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(lead.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EmployeeDashboardNew;
